/**
 * Header de Ritualic.
 *
 * Hace tres cosas y ninguna mas:
 *
 * 1. Abre y cierra el cajon del menu en movil, con Escape y clic fuera.
 * 2. Publica `--header-height` y `--header-group-height`. Los lee media pagina
 *    del tema —el sticky de la columna de producto, el resumen del carrito, la
 *    galeria— y en Horizon los escribe el componente del header nativo. Al usar
 *    header propio hay que seguir escribiendolos o esas piezas se descuadran.
 * 3. Marca `data-scrolled` para que la sombra aparezca solo al despegarse.
 *
 * El buscador y el carrito no son suyos: el boton apunta al modal y al cajon que
 * `layout/theme.liquid` ya monta en todas las paginas.
 */
class RitHeader extends HTMLElement {
  #drawer = null;
  #toggle = null;
  #observer = null;

  connectedCallback() {
    this.#drawer = this.querySelector('[data-rit-drawer]');
    this.#toggle = this.querySelector('[data-rit-drawer-toggle]');

    this.#toggle?.addEventListener('click', this.#onToggle);
    document.addEventListener('keydown', this.#onKey);
    document.addEventListener('click', this.#onOutside);
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observer = new ResizeObserver(this.#measure);
    this.#observer.observe(this);

    const group = document.getElementById('header-group');
    if (group) this.#observer.observe(group);

    this.#measure();
    this.#onScroll();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggle);
    document.removeEventListener('keydown', this.#onKey);
    document.removeEventListener('click', this.#onOutside);
    window.removeEventListener('scroll', this.#onScroll);
    this.#observer?.disconnect();
  }

  get #open() {
    return this.dataset.drawer === 'open';
  }

  #setOpen(open) {
    this.dataset.drawer = open ? 'open' : 'closed';
    this.#toggle?.setAttribute('aria-expanded', String(open));
    if (this.#drawer) this.#drawer.hidden = !open;
  }

  #onToggle = (event) => {
    event.preventDefault();
    this.#setOpen(!this.#open);
  };

  #onKey = (event) => {
    if (event.key === 'Escape' && this.#open) {
      this.#setOpen(false);
      this.#toggle?.focus();
    }
  };

  #onOutside = (event) => {
    if (this.#open && event.target instanceof Node && !this.contains(event.target)) this.#setOpen(false);
  };

  #onScroll = () => {
    this.dataset.scrolled = window.scrollY > 4 ? 'true' : 'false';
  };

  #measure = () => {
    const raiz = document.documentElement;
    const alto = Math.round(this.offsetHeight);

    // `--header-height` se escribe tambien en el body porque una regla del tema
    // lo fija ahi en 60px, y ese valor le gana al de la raiz para todo lo que
    // cuelga debajo. `--rit-header-height` es nuestra: nadie mas la toca, y es
    // la que leen las secciones propias.
    raiz.style.setProperty('--rit-header-height', `${alto}px`);
    for (const objetivo of [raiz, document.body]) {
      objetivo.style.setProperty('--header-height', `${alto}px`);
    }

    const group = document.getElementById('header-group');
    if (!group) return;

    raiz.style.setProperty('--header-group-height', `${Math.round(group.offsetHeight)}px`);

    // Lo que hay por encima del header dentro del grupo —la barra de anuncios—
    // se va hacia arriba al hacer scroll; el header se clava en el borde.
    let arriba = 0;
    let previo = this.closest('.shopify-section')?.previousElementSibling;
    while (previo) {
      arriba += previo.offsetHeight;
      previo = previo.previousElementSibling;
    }
    group.style.setProperty('--rit-sticky-offset', `-${Math.round(arriba)}px`);

    // Todo lo que tapa la pagina al cargar: anuncio mas header. `--header-group-height`
    // no sirve para esto —el grupo es sticky y su offsetHeight devuelve solo el
    // header—, y la ficha de producto necesita el numero real para calcular
    // cuanto le queda de primera pantalla.
    raiz.style.setProperty('--rit-topbar-height', `${Math.round(alto + arriba)}px`);
  };
}

if (!customElements.get('rit-header')) customElements.define('rit-header', RitHeader);
